import { useTranslation } from "react-i18next"
import {
  calliopeUserSchema,
  RoutingProfile,
  routingProfileSchema,
} from "@repo/core/models"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { z } from "zod"

import { AgentProfile } from "@/agent-state/use-fetch-agent-profile"
import { useAxiosClientContext } from "@/components/core/AxiosInstanceProvider/useAxiosClient"
import { useQueryAPI } from "@/hooks/query-api"
import { useToast } from "@/hooks/toastHook"

const agentsResponseSchema = z.object({
  data: z.object({
    agents: z.array(calliopeUserSchema),
    routingProfiles: z.array(routingProfileSchema),
  }),
})

const routingProfilesResponseSchema = z.object({
  data: z.array(routingProfileSchema),
})

export function useFetchUsersList() {
  return useQueryAPI({
    apiEndPoint: "/agents/list",
    schema: agentsResponseSchema,
  })
}

export function useFetchRoutingProfiles() {
  return useQueryAPI({
    apiEndPoint: "/routing-profiles",
    schema: routingProfilesResponseSchema,
  })
}

export function useCachedRoutingProfiles(agentProfile: AgentProfile | null) {
  const queryClient = useQueryClient()

  function readRoutingProfilesFromCache() {
    const data = queryClient.getQueryData<{ data: RoutingProfile[] }>([
      "/routing-profiles",
    ])

    return data?.data ?? []
  }

  const routingProfiles = readRoutingProfilesFromCache()

  const agentRoutingProfile = routingProfiles.find(
    (rp) => rp.id === agentProfile?.routing_profile_id,
  )

  return {
    agentRoutingProfile,
    getRoutingProfiles: readRoutingProfilesFromCache, // Getter to read fresh data from cache after revalidation (avoids stale closures)
  }
}

export function useMutationSyncUsers() {
  const { t } = useTranslation()
  const axiosClient = useAxiosClientContext()
  const toast = useToast()
  const revalidate = useRevalidateUserList()

  return useMutation({
    mutationFn: async () => {
      return await axiosClient.post("/agents/sync-users")
    },
    onError: () => {
      toast.error(t("syncAgents.error"))
    },
    onSuccess: () => {
      toast.success(t("syncAgents.success"))
      revalidate()
    },
  })
}

export function useMutationUpdateUser(cognito_user_id: string) {
  const axiosClient = useAxiosClientContext()
  const revalidateAgentProfile = useRevalidateAgentProfile()

  return useMutation({
    mutationFn: async (routing_profile_id: string) => {
      return await axiosClient.put(`/agents/${cognito_user_id}`, {
        routing_profile_id,
      })
    },
    onSuccess: revalidateAgentProfile,
  })
}

export function useMutationResendPassword(cognito_user_id: string) {
  const axiosClient = useAxiosClientContext()

  return useMutation({
    mutationFn: async () => {
      return await axiosClient.post(
        `/agents/${cognito_user_id}/resend-password`,
      )
    },
  })
}

export function useRevalidateUserList() {
  const queryClient = useQueryClient()

  return async () => {
    await queryClient.invalidateQueries({ queryKey: ["/agents/list"] })
  }
}

function useRevalidateAgentProfile() {
  const queryClient = useQueryClient()

  return async () => {
    await queryClient.invalidateQueries({ queryKey: ["/agents"] })
  }
}

export function useRevalidateRoutingProfiles() {
  const queryClient = useQueryClient()

  return async () => {
    await queryClient.invalidateQueries({ queryKey: ["/routing-profiles"] })
  }
}
